<template>
  <div class="personal-info q-pa-sm q-px-md">
    <h2 class="personal-info__title">Personal information</h2>
    <p class="personal-info__subtitle">Manage your personal account details</p>

    <div v-if="isLoading" class="personal-info__loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>

    <div v-else>
      <div class="personal-info__avatar-container">
        <div class="personal-info__avatar">
          <span>{{ userInitial }}</span>
        </div>
        <!-- <q-btn round flat size="xs" class="personal-info__avatar-edit">
          <q-icon name="edit" size="xs" color="grey-7" />
        </q-btn> -->
      </div>

      <div class="personal-info__form">
        <div class="personal-info__input-group">
          <div class="personal-info__input-label">Name</div>
          <q-input ref="nameInput" v-model="userInfo.name" class="personal-info__input" placeholder="Enter your name" outlined hide-bottom-space :rules="[val => !!val || 'Name is required']" />
        </div>

        <div class="personal-info__input-group">
          <div class="personal-info__input-label">Email address</div>
          <q-input v-model="userInfo.email" class="personal-info__input" placeholder="Enter your email address" outlined hide-bottom-space disable readonly />
        </div>

        <div class="personal-info__input-group">
          <div class="personal-info__input-label">Phone number (Optional)</div>
          <q-input v-model="userInfo.phone" class="personal-info__input" placeholder="Enter your phone number" outlined hide-bottom-space />
        </div>

        <div class="personal-info__input-group">
          <div class="personal-info__input-label">Company name (Optional)</div>
          <q-input v-model="userInfo.company" class="personal-info__input" placeholder="Enter your company name" outlined hide-bottom-space />
        </div>
      </div>

      <div class="personal-info__actions">
        <BaseButton v-if="isUserEdited" label="Save changes" :loading="isSaving" type="default" @click.native="updateUserInfo" />
      </div>
    </div>
  </div>
</template>

<script>
// Base components
import BaseButton from '@base/BaseButton.vue';

// Store
import { mapGetters } from 'vuex';

/**
 * Component for personal information tab in profile settings
 */
export default {
  name: 'PersonalInformation',
  components: {
    BaseButton
  },
  data() {
    return {
      userInfo: {
        name: '',
        email: '',
        phone: '',
        company: ''
      },
      originalUserInfo: {},
      isLoading: true, // Loading state for initial data fetch
      isSaving: false // Loading state for save operation
    };
  },
  computed: {
    ...mapGetters({
      user: 'user/getUser'
    }),
    /**
     * Returns first letter of the user's name for avatar
     */
    userInitial() {
      return this.userInfo.name ? this.userInfo.name.charAt(0).toUpperCase() : 'U';
    },
    /**
     * Check if user info has been edited
     */
    isUserEdited() {
      return JSON.stringify(this.originalUserInfo) !== JSON.stringify(this.userInfo);
    }
  },
  created() {
    // Set initial loading state
    this.isLoading = true;

    // Initialize form with user data
    setTimeout(() => {
      if (this.user) {
        this.userInfo = {
          name: this.user.name || '',
          email: this.user.email || '',
          phone: this.user.phone || '',
          company: this.user.company_name || ''
        };

        // Store original values for comparison
        this.originalUserInfo = { ...this.userInfo };
      }

      // Turn off loading state after data is loaded
      this.isLoading = false;
    }, 300); // Short timeout to make the loading state visible
  },
  methods: {
    /**
     * Update user information
     */
    updateUserInfo() {
      // Set loading state
      this.isSaving = true;

      // Create updated user object
      const updatedUser = {
        ...this.user,
        name: this.userInfo.name,
        phone: this.userInfo.phone,
        company_name: this.userInfo.company
      };

      // Emit event to parent component to handle the update
      this.$emit('update-user', updatedUser);

      // Simulate API delay for consistent UI experience
      setTimeout(() => {
        // Update original values
        this.originalUserInfo = { ...this.userInfo };

        // Reset loading state
        this.isSaving = false;

        // Show success notification
        this.$q.notify({
          type: 'positive',
          message: 'Profile information updated',
          position: 'top'
        });
      }, 600); // Timeout to simulate API delay and show loading state
    }
  }
};
</script>

<style lang="scss" scoped>
.personal-info {
  width: 100%;
  background-color: var(--secondary-bg-color);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: left;
  overflow: hidden;
  padding: 16px 24px;

  &__title {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0 0 2px 0;
  }

  &__subtitle {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0 0 16px 0;
  }

  &__avatar-container {
    position: relative;
    width: 72px;
    height: 72px;
    margin-bottom: 16px;
  }

  &__avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: var(--main-btn-color);
    display: flex;
    align-items: center;
    justify-content: center;
    @include inter-font($size: 24px, $bolded: true, $color: var(--main-text-color));
  }

  &__avatar-edit {
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 0;
    min-height: unset;
    min-width: unset;
    width: 24px;
    height: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    background-color: white;
    border: 1px solid #f2f2f2;
  }

  &__form {
    margin-bottom: 16px;
  }

  &__input-group {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__input-label {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 600);
    margin-bottom: 4px;
  }

  &__input {
    width: 100%;
    margin-bottom: 0;
    @include inter-font($size: 16px, $bolded: false, $color: var(--main-text-color));

    ::v-deep .q-field__control {
      height: 40px;
      padding: 0 12px;
      border-radius: 8px !important;
    }

    ::v-deep .q-field__native {
      padding-left: 4px;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-start;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
  }
}
</style>
